/** One engagement event, as the views endpoint takes it. */
export interface ActivityEvent {
  code: string;
  roomId: string | null;
}

/** The parts of `sessionStorage` the tracker uses; null where storage is unavailable. */
export interface ActivityStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface ActivityTrackerOptions {
  send(events: ActivityEvent[]): Promise<unknown>;
  storage: ActivityStorage | null;
  schedule(run: () => void, ms: number): unknown;
  delayMs?: number;
}

export interface ActivityTracker {
  sessionStarted(code: string): void;
  viewed(code: string, roomId?: string | null): void;
}

/** Where the keys already recorded this app session are kept — one entry per app origin. */
const SENT_KEY = 'brush.activity.sent';

/** The most events the views endpoint takes in one request (`ActivityEventService.MaxEvents`). */
const MAX_BATCH = 50;

/** How long events wait to be sent together. */
const DEFAULT_DELAY_MS = 2000;

/**
 * The client half of ADR 0012 Decision 13: one session per app session, one view per screen per
 * session, recorded on entry, batched, never retried.
 *
 * "App session" is `sessionStorage`'s: it survives a reload and ends with the tab, so a reload is not
 * a new session and a new tab is. An event is marked sent the moment it is queued, before the request
 * — so a failure is not retried, by construction rather than by a flag somebody could forget.
 */
export function createActivityTracker(options: ActivityTrackerOptions): ActivityTracker {
  const sent = new Set<string>(readSent(options.storage));
  let queue: ActivityEvent[] = [];
  let scheduled = false;

  const flush = () => {
    scheduled = false;
    const batch = queue;
    queue = [];
    if (batch.length === 0) return;
    // Never retried, and never an error in the app: the events were marked sent when queued, so a
    // failure here loses them — usage tolerates loss (Decision 1) — and is swallowed. In batches the
    // server will take, since it refuses a larger one whole.
    for (let i = 0; i < batch.length; i += MAX_BATCH) {
      options.send(batch.slice(i, i + MAX_BATCH)).catch(() => undefined);
    }
  };

  const record = (event: ActivityEvent) => {
    const key = `${event.code}|${event.roomId ?? ''}`;
    if (sent.has(key)) return;
    sent.add(key);
    writeSent(options.storage, sent);

    queue.push(event);
    if (!scheduled) {
      scheduled = true;
      options.schedule(flush, options.delayMs ?? DEFAULT_DELAY_MS);
    }
  };

  return {
    sessionStarted: (code) => record({ code, roomId: null }),
    viewed: (code, roomId = null) => record({ code, roomId }),
  };
}

// Storage can throw — a private window, blocked site data, a sandboxed preview — and a value can be
// unreadable. Either way the tracker carries on in memory for the life of the page: still once per
// page, and never an error in the app over a usage count.
function readSent(storage: ActivityStorage | null): string[] {
  try {
    const raw = storage?.getItem(SENT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
  } catch {
    return [];
  }
}

function writeSent(storage: ActivityStorage | null, sent: Set<string>): void {
  try {
    storage?.setItem(SENT_KEY, JSON.stringify([...sent]));
  } catch {
    // In memory only, as above.
  }
}

/** One tracker per API client, so every screen of an app shares one queue and one record of what went. */
const trackers = new WeakMap<object, ActivityTracker>();

/**
 * The app's tracker: sessionStorage where the browser allows it, a real timer, and the views endpoint.
 * Cached per client, since two trackers would each keep a queue and could send one event twice.
 */
export function activityTrackerFor(api: { activity: { record(events: ActivityEvent[]): Promise<unknown> } }): ActivityTracker {
  let tracker = trackers.get(api);
  if (!tracker) {
    tracker = createActivityTracker({
      send: (events) => api.activity.record(events),
      storage: browserSessionStorage(),
      schedule: (run, ms) => setTimeout(run, ms),
    });
    trackers.set(api, tracker);
  }
  return tracker;
}

function browserSessionStorage(): ActivityStorage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}
